import { z } from 'zod';
import { BusinessDateSchema, IdSchema } from './encoding.js';
/**
 * Company announcements (CLR-63; FRD-HRP-105 to HRP-107, FRD-CORE-068 to CORE-070): TAQON events and news that HR or an
 * administrator posts, shown to staff on the dashboard with the announced public holidays and
 * upcoming leave. A news item may link to an article on an external website; its thumbnail is an
 * image document attached to the announcement, so nothing is ever fetched from another site.
 */
const MasterStatusSchema = z.enum(['active', 'inactive']);
export const ANNOUNCEMENT_KINDS = ['event', 'news'];
export const AnnouncementKindSchema = z.enum(ANNOUNCEMENT_KINDS);
export const ANNOUNCEMENT_TITLE_MAX_LENGTH = 200;
export const ANNOUNCEMENT_DETAILS_MAX_LENGTH = 2000;
export const ANNOUNCEMENT_LOCATION_MAX_LENGTH = 200;
export const ANNOUNCEMENT_LINK_MAX_LENGTH = 500;
/** The most pictures an announcement shows, in the order they were uploaded (22 Oct 2026). */
export const ANNOUNCEMENT_PICTURES_MAX = 10;
/** How far ahead the noticeboard looks, in days, from the business date. */
export const NOTICEBOARD_DAYS_AHEAD = 30;
/** How long a news item without an end date stays on the noticeboard, in days from its date. */
export const NEWS_DEFAULT_DAYS = 30;
/** Only an https address to another website; never script or data addresses. */
const LinkUrlSchema = z
    .url({ protocol: /^https$/, hostname: z.regexes.domain })
    .max(ANNOUNCEMENT_LINK_MAX_LENGTH);
export const AnnouncementSchema = z.object({
    id: IdSchema,
    kind: AnnouncementKindSchema,
    title: z.string(),
    details: z.string().nullable(),
    /** An event's first day; a news item's publication date. */
    startDate: z.string(),
    /** An event's last day (null: one day); the last day a news item is shown (null: 30 days). */
    endDate: z.string().nullable(),
    linkUrl: z.string().nullable(),
    /** An event's location (null: not stated; always null for news). */
    location: z.string().nullable(),
    /** Whether TAQON attends the event (always false for news). */
    taqonAttending: z.boolean(),
    status: MasterStatusSchema,
    version: z.number().int(),
    createdAt: z.string(),
    /**
     * On the noticeboard only: whether the announcement has a picture to show, read from
     * `/hr/announcements/{id}/picture`.
     */
    hasPicture: z.boolean().optional(),
    /**
     * On the noticeboard and one announcement's read: its pictures, oldest first and at most
     * ANNOUNCEMENT_PICTURES_MAX, each read from `/hr/announcements/{id}/pictures/{pictureId}`.
     */
    pictureIds: z.array(IdSchema).max(ANNOUNCEMENT_PICTURES_MAX).optional(),
    /**
     * On the noticeboard and one announcement's read: whether it has a PDF to open, read from
     * `/hr/announcements/{id}/document`.
     */
    hasDocument: z.boolean().optional(),
});
const AnnouncementFields = {
    title: z.string().trim().min(1).max(ANNOUNCEMENT_TITLE_MAX_LENGTH),
    details: z.string().trim().max(ANNOUNCEMENT_DETAILS_MAX_LENGTH).nullable(),
    startDate: BusinessDateSchema,
    endDate: BusinessDateSchema.nullable(),
    linkUrl: LinkUrlSchema.nullable(),
    location: z.string().trim().min(1).max(ANNOUNCEMENT_LOCATION_MAX_LENGTH).nullable(),
    taqonAttending: z.boolean(),
};
export const CreateAnnouncementRequestSchema = z
    .strictObject({
    kind: AnnouncementKindSchema,
    ...AnnouncementFields,
    details: AnnouncementFields.details.optional(),
    endDate: AnnouncementFields.endDate.optional(),
    linkUrl: AnnouncementFields.linkUrl.optional(),
    location: AnnouncementFields.location.optional(),
    taqonAttending: AnnouncementFields.taqonAttending.optional(),
})
    .refine((a) => a.endDate == null || a.endDate >= a.startDate, {
    path: ['endDate'],
    message: 'The end date cannot be before the start date',
})
    .refine((a) => a.kind === 'event' || (a.location == null && a.taqonAttending !== true), {
    path: ['location'],
    message: 'Only an event has a location or TAQON attending',
});
/** Edits an announcement or withdraws it (status inactive) (`If-Match`). The kind is fixed. */
export const UpdateAnnouncementRequestSchema = z.strictObject({
    title: AnnouncementFields.title.optional(),
    details: AnnouncementFields.details.optional(),
    startDate: AnnouncementFields.startDate.optional(),
    endDate: AnnouncementFields.endDate.optional(),
    linkUrl: AnnouncementFields.linkUrl.optional(),
    location: AnnouncementFields.location.optional(),
    taqonAttending: AnnouncementFields.taqonAttending.optional(),
    status: MasterStatusSchema.optional(),
});
/** A public holiday as the noticeboard shows it. */
export const NoticeboardHolidaySchema = z.object({
    holidayDate: z.string(),
    name: z.string(),
});
/**
 * What the dashboard shows (FRD-HRP-107, FRD-CORE-069): from the business date `today` to `until`, the active
 * events that have not ended, the active news being shown today, and the active public holidays.
 */
export const NoticeboardSchema = z.object({
    today: z.string(),
    until: z.string(),
    /** Soonest first; an event under way is included. */
    events: z.array(AnnouncementSchema),
    /** Newest first. */
    news: z.array(AnnouncementSchema),
    /** Soonest first. */
    publicHolidays: z.array(NoticeboardHolidaySchema),
});
/**
 * The caller's own occasions on the business date `today` (FRD-CORE-069): worked out on the server
 * from their own employee record, so a birthday is wished without the date of birth, a masked
 * field, ever leaving the server.
 */
export const OwnOccasionsSchema = z.object({
    today: z.string(),
    /** Null for a user not linked to an employee record: they have no occasion to be wished. */
    employeeId: IdSchema.nullable(),
    birthdayToday: z.boolean(),
    /** Whole years with TAQON when today is the joining anniversary; null on any other day. */
    workAnniversaryYears: z.number().int().positive().nullable(),
});
//# sourceMappingURL=hr-announcement.js.map